/**
 * @architecture-header 1
 * @layer module
 * @domain product-updates
 * @responsibility Expose the static product-updates content and a client-side (localStorage) seen
 *   cursor -- CONTRACT APPROVED R1: no network call, no app.* table, no Vispo HTTP request anywhere
 *   in this module. The cursor is deliberately NOT persisted across sign-out (browser-storage.ts's
 *   clearBrowserTransientState wipes it), so a different identity signing into the same browser
 *   never inherits a prior identity's seen-state.
 * @state-owner localStorage key vispo:product-updates:last-seen-id
 * @allowed-imports same-module content; React
 * @forbidden network; fetch; API client; app.* table access
 * @public-api PRODUCT_UPDATES, ProductUpdate, useProductUpdatesCursor
 * @side-effects reads/writes one localStorage key
 * @tests index.test.ts
 */

import { useCallback, useMemo, useState } from 'react';
import { PRODUCT_UPDATES, type ProductUpdate } from './content';

const CURSOR_KEY = 'vispo:product-updates:last-seen-id';

function readCursor(): string | null {
  try {
    return window.localStorage.getItem(CURSOR_KEY);
  } catch {
    return null;
  }
}

function writeCursor(id: string): void {
  try {
    window.localStorage.setItem(CURSOR_KEY, id);
  } catch {
    // localStorage can throw in private-browsing/quota-exceeded contexts -- degrade to "the
    // cursor never persists this round" rather than crash the popover/page.
  }
}

// Pure and independently testable: `updates` is newest-first, so every entry strictly before the
// cursor's own index is unseen. A cursor id that matches nothing in `updates` (should not happen --
// content.ts is append-only -- but defensive regardless) treats every entry as unseen.
export function computeUnseenIds(
  updates: readonly ProductUpdate[],
  cursorId: string | null,
): ReadonlySet<string> {
  const cursorIndex =
    cursorId === null ? -1 : updates.findIndex((update) => update.id === cursorId);
  const boundary = cursorIndex === -1 ? updates.length : cursorIndex;
  return new Set(updates.slice(0, boundary).map((update) => update.id));
}

export interface ProductUpdatesCursor {
  updates: readonly ProductUpdate[];
  unseenIds: ReadonlySet<string>;
  markAllSeen(): void;
}

// content.ts is keyed 'zh-TW'/en (the international locale tag); the workspace UI store's own
// WorkspaceLanguage is the shorter 'zh'/'en' -- this is the one place that bridges the two.
export function localizedProductUpdateText(
  entry: { 'zh-TW': string; en: string },
  language: 'zh' | 'en',
): string {
  return language === 'en' ? entry.en : entry['zh-TW'];
}

export function useProductUpdatesCursor(): ProductUpdatesCursor {
  const [cursor, setCursor] = useState<string | null>(() => readCursor());
  const unseenIds = useMemo(() => computeUnseenIds(PRODUCT_UPDATES, cursor), [cursor]);
  const markAllSeen = useCallback(() => {
    const newest = PRODUCT_UPDATES[0];
    if (!newest) return;
    writeCursor(newest.id);
    setCursor(newest.id);
  }, []);
  return { updates: PRODUCT_UPDATES, unseenIds, markAllSeen };
}

export { PRODUCT_UPDATES, type ProductUpdate };
